import React, { useState, useEffect } from 'react';
import { useNavigate, useParams, Link } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPlus, faPencilAlt, faTrashAlt } from '@fortawesome/free-solid-svg-icons';

import { getCountryById, saveCountry } from '../services/CountryServices';
import { deleteCity } from '../services/CityServices';

import './Forms.css';
import styles from './Table.module.css';

function CountryForm() {
  const { id } = useParams();
  const navigate = useNavigate();

  const [cities, setCities] = useState([]);
  const citiesPerPage = 20;
  const [citySearch, setCitySearch] = useState('');
  const [cityPage, setCityPage] = useState(1);
  const [country, setCountry] = useState({
    code: '',
    isoCode: '',
    currency: '',
    flag: '',
    latitude: '',
    longitude: '',
    name: '',
    phonecode: '',
    timezones: []
  });
  const [error, setError] = useState(null);
  const filteredCities = cities.filter(city =>
    city.name.toLowerCase().includes(citySearch.toLowerCase())
  );
  const [loading, setLoading] = useState(true);
  const paginatedCities = filteredCities.slice(
    (cityPage - 1) * citiesPerPage,
    cityPage * citiesPerPage
  );
  const totalCityPages = Math.ceil(filteredCities.length / citiesPerPage);
  const [updateCounter, setUpdateCounter] = useState(0);

  // Timezone editing state
  const [editingTimezoneIdx, setEditingTimezoneIdx] = useState(null);
  const [timezoneDraft, setTimezoneDraft] = useState({
    abbreviation: '',
    gmtOffset: '',
    gmtOffsetName: '',
    tzName: '',
    zoneName: ''
  });

  useEffect(() => {
    const fetchData = async () => {
      try {
        setError(null);

        if (id) {
          const countryData = await getCountryById(id);
          setCountry({
            code: countryData.code || '',
            isoCode: countryData.isoCode || countryData.code || '',
            currency: countryData.currency || '',
            flag: countryData.flag || '',
            latitude: countryData.latitude !== undefined ? countryData.latitude : '',
            longitude: countryData.longitude !== undefined ? countryData.longitude : '',
            name: countryData.name || '',
            phonecode: countryData.phonecode || '',
            timezones: countryData.timezones || [],
            id: countryData.id
          });
          setCities(countryData.cities || []);
        }
      } catch (error) {
        setError(error.message);
        console.error('Error fetching country data:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [id, updateCounter]);

  // Mantener isoCode igual a code
  useEffect(() => {
    setCountry(prev => ({ ...prev, isoCode: prev.code }));
  }, [country.code]);

  const handleDeleteCity = async (id) => {
    setError(null);
    setLoading(true);
    try {
      await deleteCity(id);
      setUpdateCounter(updateCounter + 1);
    } catch (error) {
      setError(error.message);
    } finally {
      setLoading(false);
    }
  };

  const handleCountryChange = (e) => {
    const { name, value } = e.target;
    setCountry({ ...country, [name]: value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (id) country.id = id;
      await saveCountry(country);
      navigate('/config');
    } catch (error) {
      setError(error.message);
    }
  };

  // Timezone handlers
  const handleEditTimezone = (idx) => {
    setEditingTimezoneIdx(idx);
    setTimezoneDraft({ ...country.timezones[idx] });
  };

  const handleTimezoneDraftChange = (e) => {
    const { name, value } = e.target;
    setTimezoneDraft({ ...timezoneDraft, [name]: value });
  };

  const handleSaveTimezone = () => {
    const newTimezones = [...country.timezones];
    newTimezones[editingTimezoneIdx] = { ...timezoneDraft };
    setCountry({ ...country, timezones: newTimezones });
    setEditingTimezoneIdx(null);
    setTimezoneDraft({
      abbreviation: '',
      gmtOffset: '',
      gmtOffsetName: '',
      tzName: '',
      zoneName: ''
    });
  };

  const handleAddTimezone = () => {
    setCountry({
      ...country,
      timezones: [
        ...country.timezones,
        {
          abbreviation: '',
          gmtOffset: '',
          gmtOffsetName: '',
          tzName: '',
          zoneName: ''
        }
      ]
    });
    setEditingTimezoneIdx(country.timezones.length);
    setTimezoneDraft({
      abbreviation: '',
      gmtOffset: '',
      gmtOffsetName: '',
      tzName: '',
      zoneName: ''
    });
  };

  const handleDeleteTimezone = (idx) => {
    const newTimezones = country.timezones.filter((_, i) => i !== idx);
    setCountry({ ...country, timezones: newTimezones });
    setEditingTimezoneIdx(null);
  };

  if (loading) {
    return <div>Cargando...</div>;
  }

  return (
    <div className="page-container" style={{ display: "flex", gap: "32px" }}>
      <div className="form-container" style={{ flex: 1 }}>
        <h2>{id ? 'Editar País' : 'Agregar País'}</h2>
        {error && (
          <div>
            <div className='error-message'>
              Hubo un error: {error}
            </div>
            <br />
          </div>
        )}
        <form onSubmit={handleSubmit}>
          <div className="form-group">
            <label htmlFor="country-code">Código</label>
            <input
              type="text"
              id="country-code"
              name="code"
              value={country.code}
              onChange={handleCountryChange}
              required
            />
          </div>
          <div className="form-group">
            <label htmlFor="country-isoCode">ISO Code</label>
            <input
              type="text"
              id="country-isoCode"
              name="isoCode"
              value={country.isoCode}
              readOnly
            />
          </div>
          <div className="form-group">
            <label htmlFor="country-currency">Moneda</label>
            <input
              type="text"
              id="country-currency"
              name="currency"
              value={country.currency}
              onChange={handleCountryChange}
              required
            />
          </div>
          <div className="form-group">
            <label htmlFor="country-flag">Bandera (URL)</label>
            <input
              type="text"
              id="country-flag"
              name="flag"
              value={country.flag}
              onChange={handleCountryChange}
            />
          </div>
          <div className="form-group">
            <label htmlFor="country-latitude">Latitud</label>
            <input
              type="number"
              id="country-latitude"
              name="latitude"
              step="0.00000001"
              value={country.latitude}
              onChange={handleCountryChange}
            />
          </div>
          <div className="form-group">
            <label htmlFor="country-longitude">Longitud</label>
            <input
              type="number"
              id="country-longitude"
              name="longitude"
              step="0.00000001"
              value={country.longitude}
              onChange={handleCountryChange}
            />
          </div>
          <div className="form-group">
            <label htmlFor="country-name">Nombre</label>
            <input
              type="text"
              id="country-name"
              name="name"
              value={country.name}
              onChange={handleCountryChange}
              required
            />
          </div>
          <div className="form-group">
            <label htmlFor="country-phonecode">Código Telefónico</label>
            <input
              type="text"
              id="country-phonecode"
              name="phonecode"
              value={country.phonecode}
              onChange={handleCountryChange}
            />
          </div>
          <div className="form-group">
            <button type="submit" className="btn-submit">{id ? 'Guardar Cambios' : 'Agregar País'}</button>
          </div>
        </form>
        <div className="form-footer">
          <Link to="/config" className="back-link">Regresar a datos básicos</Link>
        </div>
      </div>

      {/* Costado derecho: Ciudades y Timezones */}
      <div style={{ flex: 1, display: "flex", flexDirection: "column", gap: "32px" }}>
        {country.id && (
          <div className="stages-container" style={{ width: "90%" }}>
            <h2>Ciudades</h2>
            <button onClick={() => navigate('/cities/new/' + id)}>
              <FontAwesomeIcon icon={faPlus} />
            </button>
            <br />
            <br />

            {/* Cuadro de búsqueda */}
            <input
              type="text"
              placeholder="Buscar ciudad..."
              value={citySearch}
              onChange={e => {
                setCitySearch(e.target.value);
                setCityPage(1);
              }}
              style={{ margin: "12px 0", padding: "6px", width: "220px" }}
            />

            <table className={styles.table} style={{ width: "90%" }}>
              <thead>
                <tr>
                  <th style={{ width: '60%' }}>Nombre</th>
                  <th>Acciones</th>
                </tr>
              </thead>
              <tbody>
                {paginatedCities.map((city) => (
                  <tr key={city.id}>
                    <td>{city.name} ({city.stateCode})</td>
                    <td>
                      <button onClick={() => navigate(`/cities/edit/${id}/${city.id}`)}>
                        <FontAwesomeIcon icon={faPencilAlt} />
                      </button>
                      <button onClick={() => handleDeleteCity(city.id)}>
                        <FontAwesomeIcon icon={faTrashAlt} />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>

            {/* Controles de paginación */}
            <div style={{ marginTop: "12px", display: "flex", gap: "8px", alignItems: "center" }}>
              <button
                disabled={cityPage === 1}
                onClick={() => setCityPage(1)}
              >
                {'<<'}
              </button>
              <button
                disabled={cityPage === 1}
                onClick={() => setCityPage(cityPage - 1)}
              >
                {'<'}
              </button>
              <span style={{ color: "black" }}>Página {cityPage} de {totalCityPages}</span>
              <button
                disabled={cityPage === totalCityPages || totalCityPages === 0}
                onClick={() => setCityPage(cityPage + 1)}
              >
                {'>'}
              </button>
              <button
                disabled={cityPage === totalCityPages || totalCityPages === 0}
                onClick={() => setCityPage(totalCityPages)}
              >
                {'>>'}
              </button>
            </div>
          </div>
        )}

        {/* Timezones */}
        <div className="stages-container" style={{ marginTop: "32px", width: "90%" }}>
          <h2>Timezones</h2>
          <button onClick={handleAddTimezone}>
            <FontAwesomeIcon icon={faPlus} /> Agregar Timezone
          </button>
          <table className={styles.table} style={{ width: "90%" }}>
            <thead>
              <tr>
                <th>Abbreviation</th>
                <th>GMT Offset</th>
                <th>GMT Offset Name</th>
                <th>TZ Name</th>
                <th>Zone Name</th>
                <th>Acciones</th>
              </tr>
            </thead>
            <tbody>
              {country.timezones.map((tz, idx) => (
                editingTimezoneIdx === idx ? (
                  <tr key={idx}>
                    <td><input type="text" name="abbreviation" value={timezoneDraft.abbreviation} onChange={handleTimezoneDraftChange} /></td>
                    <td><input type="number" name="gmtOffset" value={timezoneDraft.gmtOffset} onChange={handleTimezoneDraftChange} /></td>
                    <td><input type="text" name="gmtOffsetName" value={timezoneDraft.gmtOffsetName} onChange={handleTimezoneDraftChange} /></td>
                    <td><input type="text" name="tzName" value={timezoneDraft.tzName} onChange={handleTimezoneDraftChange} /></td>
                    <td><input type="text" name="zoneName" value={timezoneDraft.zoneName} onChange={handleTimezoneDraftChange} /></td>
                    <td>
                      <button onClick={handleSaveTimezone}><FontAwesomeIcon icon={faPencilAlt} /> Guardar</button>
                      <button onClick={() => setEditingTimezoneIdx(null)}>Cancelar</button>
                    </td>
                  </tr>
                ) : (
                  <tr key={idx}>
                    <td>{tz.abbreviation}</td>
                    <td>{tz.gmtOffset}</td>
                    <td>{tz.gmtOffsetName}</td>
                    <td>{tz.tzName}</td>
                    <td>{tz.zoneName}</td>
                    <td>
                      <button onClick={() => handleEditTimezone(idx)}><FontAwesomeIcon icon={faPencilAlt} /></button>
                      <button onClick={() => handleDeleteTimezone(idx)}><FontAwesomeIcon icon={faTrashAlt} /></button>
                    </td>
                  </tr>
                )
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

export default CountryForm;